import React, { Component } from "react";
import { connect } from "react-redux";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { solid } from "@fortawesome/fontawesome-svg-core/import.macro";
import { DateTime } from "luxon";
import DataBase from "../dataBase/liquidGuides";
import Guide from "../Models/Guide";
import Common from "../Common/Common";
import { stableEventKey } from "../Common/GuideDiff";
import "./CommentsSection.css";

// Lightweight comment thread, dropped into a Place's or Event's own edit
// modal as one more labeled section (same "scroll of cards" convention both
// PlaceEditModal.js and EventEdit.js already use) — not a separate dialog
// like ReviewsDialog.js, since there's no rating to pick, just free text.
// Self-connects to Redux for guide_info/guide_info_id/user_info rather than
// requiring every one of PlaceEditModal's/EventEdit's several call sites to
// thread new props through.
//
// Takes the actual place/event object (`item`), not a raw ID: a Place's own
// `.ID` IS stable across decode/reload, but an Event's `.ID` is NOT — it's
// regenerated randomly on every guide decode (Event.js's
// json_to_appointment), the exact same instability GuideDiff.js already had
// to work around for its own event matching. Computing the right stable key
// here, once, means no caller has to know about this quirk.
class CommentsSection extends Component {
  constructor(props) {
    super(props);
    this.state = {
      comments: null,
      draft: "",
      posting: false,
      postError: null,
    };
  }

  componentDidMount() {
    this.loadComments();
  }

  getTargetId = () => {
    const { targetType, item, guide_info_id } = this.props;
    // Guide-level thread: target_id is the guide's own id, self-referential
    // (one thread per guide) — no item object to resolve an ID from, unlike
    // place/event.
    if (targetType === "guide") return guide_info_id;
    if (!item) return null;
    return targetType === "event" ? (item.CommentKey || stableEventKey(item)) : item.ID;
  };

  loadComments = () => {
    const { guide_info_id, targetType } = this.props;
    const targetId = this.getTargetId();
    if (!guide_info_id || !targetId) {
      this.setState({ comments: [] });
      return;
    }
    DataBase.getComments(guide_info_id, targetType, targetId, this.getCommentsCallback);
  };

  getCommentsCallback = (res) => {
    if (res.pass !== true) return;
    this.setState({ comments: res.comments });
  };

  // Posting requires access granted BY NAME (owner, or uid / uid:R/C/M) —
  // a community "Share with all" grant (All_R/All_C/All_M) lets anyone read
  // the guide but not comment on it, same as polls. Matches the Firestore
  // comments create rule (hasPersonalAccess). 2026-10-09: comments are for
  // a trip's own group; public guides would otherwise collect comments from
  // strangers. Reading stays open.
  canPost = () => Guide.hasPersonalAccess(this.props.guide_info, this.props.user_info?.uid);

  canModerate = () => {
    const { guide_info, user_info } = this.props;
    if (!guide_info || !user_info) return false;
    if (guide_info.user_id === user_info.uid) return true;
    return Guide.hasPersonalModifyAccess(guide_info, user_info.uid);
  };

  handlePost = () => {
    const { guide_info_id, targetType, user_info } = this.props;
    const targetId = this.getTargetId();
    const text = this.state.draft.trim();
    if (text === "" || !guide_info_id || !targetId || user_info.uid === "guest" || !this.canPost()) return;
    const name = `${user_info.first_name || ""} ${user_info.last_name || ""}`.trim();
    const comment = {
      target_type: targetType,
      target_id: targetId,
      text: text,
      user_id: user_info.uid,
      user_name: name || user_info.email,
      date: DateTime.utc().toISO(),
    };
    this.setState({ posting: true });
    DataBase.addComment(guide_info_id, comment, (res) => {
      this.setState({ posting: false });
      // Say why a post failed instead of silently dropping it — most often
      // the Firestore rules refusing it (comments need access granted by
      // name; also hit by an older app version or access removed while the
      // guide was open). 2026-10-09.
      if (res.pass !== true) {
        const denied = res.error?.code === "permission-denied";
        this.setState({
          postError: denied
            ? "You can't comment on this guide — only people it's shared with by name can comment."
            : "Couldn't post your comment. Check your connection and try again.",
        });
        return;
      }
      this.setState({ draft: "", postError: null });
      this.loadComments();
    });
  };

  handleDelete = (comment_id) => {
    const { guide_info_id } = this.props;
    DataBase.deleteComment(guide_info_id, comment_id, (res) => {
      if (res.pass !== true) return;
      this.loadComments();
    });
  };

  render() {
    const { comments } = this.state;
    const { user_info } = this.props;
    const canModerate = this.canModerate();
    const canPost = this.canPost();
    return (
      <>
        <span className="cs-section-label">Comments</span>
        <div className="cs-card">
          {comments == null ? (
            <div className="cs-empty">Loading…</div>
          ) : comments.length === 0 ? (
            <div className="cs-empty">No comments yet.</div>
          ) : (
            comments.map((item) => (
              <div className="cs-item" key={item.id}>
                <div className="cs-item-head">
                  <span className="cs-item-name">{item.data.user_name}</span>
                  <span className="cs-item-date">{Common.formatDateLuxon(item.data.date)}</span>
                  {item.data.user_id === user_info.uid || canModerate ? (
                    <FontAwesomeIcon
                      icon={solid("trash")}
                      className="cs-item-delete"
                      onClick={() => this.handleDelete(item.id)}
                    />
                  ) : null}
                </div>
                <div className="cs-item-text">{item.data.text}</div>
              </div>
            ))
          )}
          {canPost ? (
            <div className="cs-compose">
              <textarea
                className="cs-compose-input"
                placeholder="Add a comment…"
                value={this.state.draft}
                onChange={(e) => this.setState({ draft: e.target.value, postError: null })}
              />
              <button
                className="cs-compose-submit"
                disabled={this.state.draft.trim() === "" || this.state.posting}
                onClick={this.handlePost}
              >
                Post
              </button>
            </div>
          ) : user_info.uid !== "guest" && comments != null ? (
            <div className="cs-empty">Only people this guide is shared with by name can comment.</div>
          ) : null}
          {canPost && this.state.postError ? <div className="cs-error">{this.state.postError}</div> : null}
        </div>
      </>
    );
  }
}

const mapStateToProps = (state) => ({
  guide_info_id: state.guide_meta.guide_info_id,
  guide_info: state.guide_meta.guide_info,
  user_info: state.user_info,
});

export default connect(mapStateToProps)(CommentsSection);
